import React, { forwardRef, useId, useRef } from 'react';
import { Check } from 'lucide-react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SIZE = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const LABEL_SIZE = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

const CheckBox = forwardRef(function CheckBox(
  {
    label,
    helperText,
    id: idProp,
    name,
    value,
    checked,
    defaultChecked,
    onChange,
    onBlur,
    onFocus,
    disabled = false,
    required = false,
    size = 'md',
    color = 'primary',
    className,
    containerClassName,
    labelClassName,
    inputClassName,
    style,
    indeterminate = false,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const id = idProp || `checkbox-${reactId}`;
  const inputRef = useRef(null);

  const sizeClasses = SIZE[size];
  const labelSizeClasses = LABEL_SIZE[size];

  // Brand colors matching your project
  const brandColors = {
    primary: '#1575DB',
    secondary: '#3a4878',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b',
    neutral: '#6b7280',
  };

  const checkboxClasses = cn(
    sizeClasses,
    'rounded border-2 border-gray-300 transition-all cursor-pointer',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:bg-gray-100 disabled:border-gray-300 disabled:cursor-not-allowed',
    checked || indeterminate ? '' : 'bg-white hover:border-gray-400',
    inputClassName
  );

  // Inline styles for checked state to use brand colors
  const checkedStyle = checked || indeterminate ? {
    backgroundColor: brandColors[color] || brandColors.primary,
    borderColor: brandColors[color] || brandColors.primary,
  } : {};

  return (
    <div className={cn('flex flex-col', containerClassName)} style={style}>
      <div className="flex items-center gap-3">
        <div className="relative flex items-center">
          <input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            id={id}
            name={name}
            type="checkbox"
            value={value}
            checked={checked !== undefined ? checked : defaultChecked}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={disabled}
            required={required}
            className={cn('sr-only')}
            {...rest}
          />
          <div
            className={checkboxClasses}
            style={checkedStyle}
            onClick={(e) => {
              if (!disabled && inputRef.current) {
                inputRef.current.click();
              }
            }}
          >
            {((checked !== undefined ? checked : defaultChecked) || indeterminate) && (
              <Check
                size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16}
                className="text-white absolute inset-0 m-auto"
              />
            )}
          </div>
          <style>{`
            #${id}:checked ~ div,
            #${id}:indeterminate ~ div {
              background-color: currentColor;
            }
            #${id}:disabled ~ div {
              opacity: 0.5;
            }
          `}</style>
        </div>

        {label && (
          <label
            htmlFor={id}
            className={cn(
              'cursor-pointer user-select-none',
              labelSizeClasses,
              disabled ? 'text-gray-500 cursor-not-allowed' : 'text-gray-900',
              labelClassName
            )}
          >
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </label>
        )}
      </div>

      {helperText && (
        <p className="mt-1 text-sm text-gray-600">{helperText}</p>
      )}
    </div>
  );
});

CheckBox.displayName = 'CheckBox';

export default CheckBox;
